<template>
  <div
    class="w-full max-w-md mx-auto bg-[#1F1F1F] text-white rounded-2xl shadow-2xl p-6 sm:p-8 relative"
  >
    <!-- Close Button -->
    <button
      class="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl font-bold"
      @click.prevent="$emit('close')"
    >
      ×
    </button>

    <!-- Logo and Heading -->
    <div class="flex items-center gap-2 mb-4">
      <Icon icon="mdi:account-plus" width="24" height="24" />
      <h1 class="font-semibold text-lg text-white">
        {{ t("register.title") }}
      </h1>
    </div>

    <p class="text-sm text-gray-400 mb-6">
      {{ t("register.description") }}
    </p>

    <!-- Google Signup -->
    <div class="space-y-3 mb-6">
      <button
        class="w-full flex items-center justify-center gap-2 border border-[#444] text-white bg-[#1F1F1F] hover:bg-[#2A2A2A] font-medium py-2 rounded-md transition"
        @click="socialSignup('google')"
      >
        <Icon icon="mdi:google" class="w-5 h-5" />
        {{ t("register.google") }}
      </button>
    </div>

    <div class="text-center text-sm text-gray-500 my-4">
      {{ t("register.or") }}
    </div>

    <!-- Email Input -->
    <div class="relative mb-4">
      <input
        :value="props.form.email"
        type="email"
        :placeholder="t('register.email')"
        class="w-full pl-10 pr-4 py-3 rounded-md border border-[#2A2A2A] bg-[#2A2A2A] text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3DCBB1]"
        @input="handleEmailInput"
      />
      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        <Icon icon="mdi:email-outline" class="w-4 h-4" />
      </span>
    </div>

    <!-- Password Input -->
    <div class="relative mb-6">
      <input
        :value="props.form.password"
        type="password"
        :placeholder="t('register.password')"
        class="w-full pl-10 pr-4 py-3 rounded-md border border-[#2A2A2A] bg-[#2A2A2A] text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3DCBB1]"
        @input="handlePasswordInput"
      />
      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        <Icon icon="mdi:lock-outline" class="w-4 h-4" />
      </span>
    </div>

    <!-- Signup Button -->
    <button
      class="w-full bg-[#3DCBB1] hover:bg-[#32b0a0] text-white font-semibold py-3 rounded-md transition duration-200"
      @click="signup"
    >
      {{ t("register.submit") }}
    </button>

    <div class="text-center mt-6 text-sm text-gray-400">
      {{ t("register.have_account") }}
      <button
        class="text-[#3DCBB1] hover:underline font-medium"
        @click.prevent="$emit('change')"
      >
        {{ t("register.login") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { useSupabaseClient, useI18n } from "#imports";

const { t } = useI18n();
const props = defineProps<{
  form: { email: string; password: string };
}>();

const emit = defineEmits(["update:form", "close", "change", "alert"]);
const supabase = useSupabaseClient();

const handleEmailInput = (e: Event) => {
  emit("update:form", {
    ...props.form,
    email: (e.target as HTMLInputElement).value,
  });
};

const handlePasswordInput = (e: Event) => {
  emit("update:form", {
    ...props.form,
    password: (e.target as HTMLInputElement).value,
  });
};

const signup = async () => {
  if (!props.form.email || !props.form.password) {
    emit("alert", {
      message: t("errors.missing_fields"),
      icon: "mdi:alert-circle",
      type: "error",
    });
    return;
  }

  const { data, error } = await supabase.auth.signUp({
    email: props.form.email,
    password: props.form.password,
    options: {
      emailRedirectTo: `https://mem22.com/confirm?type=signup&email=${encodeURIComponent(
        props.form.email
      )}`,
    },
  });

  if (error) {
    const msg = error.message.toLowerCase();
    let translated = t("errors.signup_failed");

    if (msg.includes("user already registered")) {
      translated = t("errors.user_exists");
    } else if (msg.includes("invalid email")) {
      translated = t("errors.invalid_email");
    } else if (msg.includes("password should be at least")) {
      translated = t("errors.weak_password");
    }

    emit("alert", {
      message: translated,
      icon: "mdi:alert-circle",
      type: "error",
    });
    return;
  }

  if (data?.user) {
    emit("alert", {
      message: t("alerts.signup_success"),
      icon: "mdi:check-circle",
      type: "success",
    });
    emit("close");
  }
};

const socialSignup = async (provider: "google") => {
  const { error } = await supabase.auth.signInWithOAuth({ provider });

  if (error) {
    emit("alert", {
      message: t("errors.social_signup_failed", { provider }),
      icon: "mdi:alert-circle",
      type: "error",
    });
  }
};
</script>
